import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { CHART_AXIS_TEXT, chartSeries } from '@/shared/lib/chart-colors';
import { formatNumber } from '@/shared/lib/format';

/** One ranked bar: a name and a count. */
export interface TopEntity {
  name: string;
  value: number;
}

/**
 * Ranked horizontal bars.
 *
 * Horizontal because the labels are brand names — rotated vertical labels are
 * the single most common readability failure in a "top N" chart.
 *
 * One colour for every bar, not one per bar: the bars are ranked instances of
 * the same measure, and colouring them differently would imply a category
 * distinction that does not exist. Length already encodes the value.
 */
export function TopEntitiesChart({
  title,
  data,
  isLoading,
  error,
  onRetry,
  unit,
  seriesIndex = 0,
}: {
  title: string;
  data: TopEntity[] | undefined;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** What the value counts, shown in the tooltip ("orders"). */
  unit: string;
  seriesIndex?: number;
}) {
  const color = chartSeries(seriesIndex);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-56 w-full" />
        ) : error ? (
          <ErrorState error={error} onRetry={onRetry} className="h-56" />
        ) : !data || data.length === 0 ? (
          <EmptyState title="Nothing recorded in this period" className="h-56" />
        ) : (
          <ResponsiveContainer width="100%" height={224}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 0, right: 12, bottom: 0, left: 0 }}
              barCategoryGap={8}
            >
              {/* No grid and no value axis: the tooltip carries exact figures,
                  and on a ranked bar chart the comparison is between bar
                  lengths, which gridlines do not help. */}
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                stroke={CHART_AXIS_TEXT}
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={110}
              />

              <Tooltip
                cursor={{ fill: 'var(--muted)', opacity: 0.5 }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const entity = payload[0]?.payload as TopEntity | undefined;
                  if (!entity) return null;
                  return (
                    <div className="bg-popover text-popover-foreground rounded-md border px-2.5 py-1.5 text-xs shadow-md">
                      <p className="font-medium">{entity.name}</p>
                      <p className="text-muted-foreground mt-0.5 tabular-nums">
                        {formatNumber(entity.value)} {unit}
                      </p>
                    </div>
                  );
                }}
              />

              <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={18}>
                {data.map((entity) => (
                  <Cell key={entity.name} fill={color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
